import Box from "@material-ui/core/Box";
import makeStyles from "@material-ui/core/styles/makeStyles";
import PropTypes from "prop-types";
import React, { Suspense, useEffect, useState } from "react";
import { Prompt, useHistory } from "react-router";
import { v4 as uuid } from "uuid";

import Toast from "../../Components/Toast";
import useActiveQueryEditor from "../../hooks/useActiveQueryEditor";
import useToast from "../../hooks/useToast";
import { DEFAULT_STRINGS, noop } from "../../utils/constants/common";
import { TOAST_ERROR } from "../../utils/constants/ToastConstants";
import EditorControls from "./EditorControls";
import EditorLoader from "./EditorLoader";

// Lazy loading Editor
const LazyEditor = React.lazy(() => import("./LazyEditor"));

/**
 * Material Ui recommend writing css styles in hook style  (css in js)
 * Can use Styled Components as well
 * It helps in making it easier to manage and make changes to the components
 * and prevent confliciting styles
 */
const useStyles = makeStyles((theme) => ({
  editorStyles: {
    border: `1px solid ${theme.palette.divider}`,
    borderRight: "0",
  },
}));

const QueryEditor = ({ isLoading, onRunQuery = noop, disabled }) => {
  const classes = useStyles();
  const history = useHistory();

  const {
    currentQuery,
    handleQueryChange,
    editorTabs,
    hasChanges,
    updateEditorTabs,
  } = useActiveQueryEditor();
  const { isToastVisible, showToast, toastType, toastMessage } = useToast();

  const handleRunQuery = () => {
    if (!currentQuery) {
      showToast(TOAST_ERROR, "Please Enter Query");
      return;
    }
    onRunQuery(currentQuery);
  };

  const [canNavigateAway, setCanNavigateAway] = useState(false);

  const handleCreateEventDef = () => {
    // Don't need to prompt before navigating away since the query won't be lost
    setCanNavigateAway(true);
    // HACK: small delay so that react has time to re-render and remove the
    // Prompt
    setTimeout(() => {
      let params = { type: "scheduled_event" };
      if (currentQuery) {
        params["typeSpecificProperty"] = currentQuery;
      }
      history.push(
        "/events/schema/new?" + new URLSearchParams(params).toString()
      );
    }, 100);
  };

  // Prompt the user to save their queries before navigating away. This effect
  // sets up a handler in the browser to prompt before the page unloads, e.g. if
  // the user reloads the page or navigates to another site.
  useEffect(() => {
    if (hasChanges) {
      window.onbeforeunload = () => true;
      return function () {
        window.onbeforeunload = undefined;
      };
    } else {
      window.onbeforeunload = undefined;
    }
  }, [hasChanges]);

  return (
    <Box>
      {/* Prompt the user to save their queries before navigating away. This
      component shows a prompt before react routes to a new page, e.g. if the
      user clicks a link in the sidebar */}
      <Prompt
        when={hasChanges && !canNavigateAway}
        message="It looks like you wrote a query or two. They will be discarded when you leave the page."
      />
      <EditorControls
        editorTabs={editorTabs}
        updateEditorTabs={updateEditorTabs}
        isLoading={isLoading}
        onRunQuery={handleRunQuery}
        onCreateEventDef={handleCreateEventDef}
        disabled={disabled}
      />
      <Suspense fallback={<EditorLoader />}>
        <LazyEditor
          aria-label="query editor input"
          mode="mysql"
          theme="tomorrow"
          name={uuid()}
          fontSize={16}
          maxLines={15}
          minLines={15}
          width="100%"
          showPrintMargin={false}
          showGutter
          highlightActiveLine={false}
          placeholder={DEFAULT_STRINGS.QUERY_EDITOR_PLACEHOLDER}
          editorProps={{ $blockScrolling: true }}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
          }}
          value={currentQuery}
          onChange={handleQueryChange}
          className={classes.editorStyles}
          showLineNumbers
        />
      </Suspense>
      <Toast show={isToastVisible} type={toastType} message={toastMessage} />
    </Box>
  );
};

export default QueryEditor;

QueryEditor.propTypes = {
  onRunQuery: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
